import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, Navigate } from "react-router";
import { clsx } from "clsx";
import { useFilterSources } from "../filters/useFilterSources";
import { HelpLink } from "../app/HelpLink";
import { GridLayout, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import { useCoreDoc } from "../app/useCoreDoc";
import { useViewCtx } from "../app/useViewCtx";
import { useDocSelector, useDocState } from "../core/useDocSelector";
import { getViewType } from "../registry/viewTypes";
import { Pill } from "../ui";
import {
  addWidget,
  nextSlot,
  deleteDashboard,
  restoreDashboard,
  listDashboards,
  removeWidget,
  setLayout,
  updateDashboard,
  updateWidget,
  type Dashboard,
  type Widget,
  dashboardTitle,
} from "./model";
import { ConfigForm, type FormSources } from "./ConfigForm";
import { listViewTypes, subscribeViewTypes, viewTypesVersion } from "../registry/viewTypes";
import { listPropDefs } from "../core/ops";
import { atLeast } from "../app/settings";
import { useToasts } from "../ui/toasts";
import { Button, Input, usePopover } from "../ui";
import { Pencil, Plus, Settings2, Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router";
import { ensureStarterDashboard } from "./starter";

/**
 * `/d/:slug` (spec §9.7): renders a dashboard's widgets on the grid; `?kiosk=1` hides the chrome and
 * sets the ≥ 24 px kiosk type (§17). The last-opened slug is remembered per device (I-7).
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const kiosk = params.get("kiosk") === "1";
  const { handle, workspaceId } = useCoreDoc();
  const { settled } = useDocState(handle);
  const ctx = useViewCtx(handle, workspaceId ?? "");
  const dashboards = useDocSelector(handle, (doc) => listDashboards(doc)) ?? [];
  const dashboard = slug ? dashboards.find((d) => d.slug === slug) : undefined;

  useEffect(() => {
    document.documentElement.dataset.kiosk = kiosk ? "true" : "false";
    return () => {
      delete document.documentElement.dataset.kiosk;
    };
  }, [kiosk]);

  // a fresh workspace gets the starter dashboard (spec §9.24), written once by the first device
  useEffect(() => {
    if (handle && settled && !handle.readOnly && dashboards.length === 0) ensureStarterDashboard(handle.doc);
  }, [handle, settled, dashboards.length]);

  const [editing, setEditing] = useState(false);
  // stacked in one column (a narrow screen): nothing to arrange there, so a phone never moves a widget by accident
  const [stacked, setStacked] = useState(false);
  const pushToast = useToasts((s) => s.push);
  const navigate = useNavigate();

  if (!settled || !ctx) return <p className="p-8 text-neutral-500">{t("session.loading")}</p>;
  const canEdit = !kiosk && !ctx.readOnly && atLeast(ctx.level, "standard") && !stacked;
  const couldEdit = !kiosk && !ctx.readOnly && atLeast(ctx.level, "standard");
  if (!dashboard) {
    if (dashboards.length > 0 && !slug) return <Navigate to={`/d/${dashboards[0].slug}`} replace />;
    return dashboards.length === 0 ? (
      <p className="p-8 text-neutral-500">{t("session.loading")}</p>
    ) : (
      <Navigate to="/" replace />
    );
  }
  return (
    <div className="flex min-h-full">
      <div className={kiosk ? "w-full p-4" : "w-full px-4 pb-16 pt-5 mid:px-7 mid:pt-6"}>
        {!kiosk && (
          <div className="mb-4 flex items-center gap-3">
            {editing && canEdit ? (
              <Input
                aria-label={t("dashboards.title")}
                className="max-w-xs text-[17px] font-semibold"
                defaultValue={dashboard.title}
                data-testid="dashboard-title-input"
                onBlur={(e) =>
                  e.target.value.trim() &&
                  e.target.value !== dashboard.title &&
                  updateDashboard(ctx.doc, dashboard.id, { title: e.target.value.trim() })
                }
              />
            ) : (
              <h1 className="text-[26px] font-semibold">{dashboardTitle(t, dashboard)}</h1>
            )}
            {!kiosk && <HelpLink page="guides/dashboards/" />}
            {handle?.readOnly && <Pill tone="warn">{t("sync.readOnly")}</Pill>}
            {couldEdit && stacked && (
              <p
                className="ml-auto text-right text-[12px] text-neutral-500"
                data-testid="dashboard-arrange-wider"
              >
                {t("dashboards.arrangeWider")}
              </p>
            )}
            {canEdit && (
              <div className="ml-auto flex items-center gap-2">
                {editing && (
                  <AddWidget
                    level={ctx.level}
                    onAdd={(viewType) =>
                      addWidget(ctx.doc, dashboard.id, { viewType, ...nextSlot(dashboard), config: {} })
                    }
                  />
                )}
                {editing && (
                  <Button
                    variant="danger"
                    size="sm"
                    data-testid="dashboard-delete"
                    onClick={() => {
                      const snapshot = dashboard;
                      deleteDashboard(ctx.doc, dashboard.id);
                      pushToast(t("dashboards.deletedToast", { title: snapshot.title }), {
                        action: {
                          label: t("common.undo"),
                          // the inverse of this delete, not the general undo (which reverts whatever this
                          // device changed last)
                          run: () => restoreDashboard(ctx.doc, snapshot),
                        },
                      });
                      navigate("/");
                    }}
                  >
                    <Trash2 size={14} aria-hidden /> {t("common.delete")}
                  </Button>
                )}
                <Button
                  variant={editing ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setEditing(!editing)}
                  data-testid="dashboard-edit"
                  aria-pressed={editing}
                >
                  {editing ? <Check size={14} aria-hidden /> : <Pencil size={14} aria-hidden />}
                  {editing ? t("dashboards.doneEditing") : t("dashboards.edit")}
                </Button>
              </div>
            )}
          </div>
        )}
        <DashboardGrid
          dashboard={dashboard}
          ctx={ctx}
          kiosk={kiosk}
          editing={editing && canEdit}
          onLayout={(l) => editing && canEdit && setLayout(ctx.doc, dashboard.id, l)}
          onStacked={setStacked}
        />
      </div>
    </div>
  );
}

export function DashboardGrid({
  dashboard,
  ctx,
  kiosk,
  editing,
  onLayout,
  onStacked,
}: {
  dashboard: Dashboard;
  ctx: NonNullable<ReturnType<typeof useViewCtx>>;
  kiosk?: boolean;
  editing?: boolean;
  onLayout?: (l: { i: string; x: number; y: number; w: number; h: number }[]) => void;
  /** told whether the widgets are stacked in one column */
  onStacked?: (stacked: boolean) => void;
}) {
  const { t } = useTranslation();
  // measured before the first layout: drawn at the default 1280 px first, widgets flashed past the edge on load
  const { width, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true });
  const widgets = useMemo(() => Object.values(dashboard.widgets), [dashboard.widgets]);
  const layout = useMemo(
    () =>
      widgets.map((w) => {
        const vt = getViewType(w.viewType);
        return {
          i: w.id,
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h,
          minW: vt?.minSize?.w ?? 2,
          minH: vt?.minSize?.h ?? 2,
          static: !editing,
        };
      }),
    [widgets, editing],
  );
  const cols = dashboard.cols || 12;
  // predictable layout (§9.19): never reflow below the configured column count; stack below 600 px
  const narrow = mounted && width < 600;
  useEffect(() => onStacked?.(narrow), [narrow, onStacked]);
  const rowHeight = kiosk
    ? Math.max(40, Math.floor((window.innerHeight - 32) / 24))
    : dashboard.rowHeight || 40;

  return (
    <div ref={containerRef} data-testid="dashboard-grid" data-slug={dashboard.slug}>
      {mounted && !narrow && (
        <GridLayout
          width={width}
          layout={layout}
          gridConfig={{ cols, rowHeight, margin: [12, 12], containerPadding: [0, 0] }}
          dragConfig={{ enabled: Boolean(editing), handle: ".drang-widget-handle" }}
          resizeConfig={{ enabled: Boolean(editing) }}
          onLayoutChange={(l) => onLayout?.(l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })))}
        >
          {widgets.map((w) => (
            <div key={w.id} className="drang-widget" data-testid="widget" data-view-type={w.viewType}>
              <WidgetFrame widget={w} ctx={ctx} editing={editing} dashId={dashboard.id} cols={cols} />
            </div>
          ))}
        </GridLayout>
      )}
      {mounted && narrow && (
        <div className="flex flex-col gap-3">
          {widgets
            .slice()
            .sort((a, b) => a.y - b.y || a.x - b.x)
            .map((w) => (
              <div
                key={w.id}
                className="drang-widget"
                data-testid="widget"
                data-view-type={w.viewType}
                style={{ minHeight: w.h * 40 }}
              >
                <WidgetFrame widget={w} ctx={ctx} editing={editing} dashId={dashboard.id} cols={cols} />
              </div>
            ))}
        </div>
      )}
      {mounted && widgets.length === 0 && (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-12 text-center text-neutral-500 dark:border-neutral-700">
          {t("dashboards.empty")}
        </div>
      )}
    </div>
  );
}

function WidgetFrame({
  widget,
  ctx,
  editing,
  dashId,
  cols,
}: {
  widget: Widget;
  ctx: NonNullable<ReturnType<typeof useViewCtx>>;
  editing?: boolean;
  dashId: string;
  /** the dashboard's column count: the most a widget can be wide */
  cols: number;
}) {
  const { t } = useTranslation();
  useSyncExternalStore(subscribeViewTypes, viewTypesVersion, viewTypesVersion); // plugins register after boot
  const vt = getViewType(widget.viewType);
  const config = { ...(vt?.defaultConfig ?? {}), ...widget.config };
  const { open, toggle, ref, panelRef } = usePopover<HTMLDivElement>();
  // the widget box clips (and the grid moves widgets with transforms), so the settings popover is
  // rendered at the page level, anchored below its button and as tall as the window allows
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const sources = useDocSelector(ctx.handle, (doc): FormSources => ({
    propDefs: listPropDefs(doc),
    groups: doc.getMap("groups").toJSON() as FormSources["groups"],
    sequences: doc.getMap("sequences").toJSON() as FormSources["sequences"],
    counters: doc.getMap("counters").toJSON() as FormSources["counters"],
  }));
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {(editing || widget.title || widget.titleKey) && (
        <div
          className={`mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500 ${editing ? "rounded-sm border border-dashed border-neutral-300 px-2 py-1 dark:border-neutral-700" : ""}`}
        >
          <span className={`min-w-0 flex-1 truncate ${editing ? "drang-widget-handle cursor-move" : ""}`}>
            {widget.title || (widget.titleKey ? t(widget.titleKey) : vt ? t(vt.label) : widget.viewType)}
          </span>
          {editing && (
            <div ref={ref} className="relative flex items-center gap-1 normal-case tracking-normal">
              <button
                type="button"
                aria-label={t("dashboards.configure")}
                aria-expanded={open}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setAnchor({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
                  toggle();
                }}
                data-testid="widget-configure"
                className="grid h-6 w-6 place-items-center rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <Settings2 size={13} aria-hidden />
              </button>
              <button
                type="button"
                aria-label={t("common.remove")}
                onClick={() => removeWidget(ctx.doc, dashId, widget.id)}
                data-testid="widget-remove"
                className="grid h-6 w-6 place-items-center rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <Trash2 size={13} aria-hidden />
              </button>
              {open &&
                sources &&
                anchor &&
                createPortal(
                  <div
                    ref={(el) => {
                      panelRef.current = el;
                    }}
                    role="dialog"
                    aria-label={t("dashboards.configure")}
                    style={{
                      top: anchor.top,
                      right: anchor.right,
                      maxHeight: `calc(100dvh - ${anchor.top + 12}px)`,
                    }}
                    className="fixed z-50 flex w-96 flex-col gap-2.5 overflow-auto *:shrink-0 rounded-md border border-neutral-200 bg-[var(--surface)] p-3 text-sm font-normal text-neutral-900 shadow-[var(--shadow-md)] dark:border-neutral-700 dark:text-neutral-100"
                  >
                    <label className="grid grid-cols-[minmax(6rem,auto)_minmax(0,1fr)] items-center gap-x-3 text-[12.5px] text-neutral-500">
                      {t("dashboards.widgetTitle")}
                      <Input
                        compact
                        defaultValue={widget.title ?? ""}
                        onBlur={(e) => updateWidget(ctx.doc, dashId, widget.id, { title: e.target.value })}
                      />
                    </label>
                    {/* the keyboard (and touch) route for the resize corner */}
                    <div className="grid grid-cols-2 gap-x-3">
                      <SizeField
                        label={t("dashboards.widgetWidth")}
                        value={widget.w}
                        min={vt?.minSize?.w ?? 2}
                        max={cols}
                        onChange={(w) => updateWidget(ctx.doc, dashId, widget.id, { w })}
                        testId="widget-size-w"
                      />
                      <SizeField
                        label={t("dashboards.widgetHeight")}
                        value={widget.h}
                        min={vt?.minSize?.h ?? 2}
                        max={48}
                        onChange={(h) => updateWidget(ctx.doc, dashId, widget.id, { h })}
                        testId="widget-size-h"
                      />
                    </div>
                    <WidgetConfigForm
                      schema={vt?.configSchema}
                      value={widget.config}
                      sources={sources}
                      ctx={ctx}
                      onChange={(next) => updateWidget(ctx.doc, dashId, widget.id, { config: next })}
                    />
                  </div>,
                  document.body,
                )}
            </div>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {vt ? (
          <vt.Component config={config} ctx={ctx} title={undefined} editing={editing} widgetId={widget.id} />
        ) : (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-4 py-6 text-center text-[12.5px] text-neutral-500 dark:border-neutral-700">
            {t("dashboards.viewTypeMissing", { id: widget.viewType })}
          </div>
        )}
      </div>
    </div>
  );
}

/** The widget's settings form; the filter builder's choices are gathered only while it is shown. */
function WidgetConfigForm({
  schema,
  value,
  sources,
  ctx,
  onChange,
}: {
  schema: Parameters<typeof ConfigForm>[0]["schema"];
  value: Record<string, unknown>;
  sources: FormSources;
  ctx: NonNullable<ReturnType<typeof useViewCtx>>;
  onChange(next: Record<string, unknown>): void;
}) {
  const filter = useFilterSources(ctx.handle, ctx.level);
  return <ConfigForm schema={schema} value={value} sources={{ ...sources, filter }} onChange={onChange} />;
}

/** A widget's width or height in grid units: written as soon as it is valid, the draft kept while typing. */
function SizeField({
  label,
  value,
  min,
  max,
  onChange,
  testId,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange(n: number): void;
  testId: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const n = draft === null ? value : Number(draft);
  const invalid = draft !== null && (draft === "" || !Number.isInteger(n) || n < min || n > max);
  return (
    <label className="grid grid-cols-[minmax(0,auto)_4.5rem] items-center gap-x-2 gap-y-0.5 text-[12.5px] text-neutral-500">
      {label}
      <Input
        aria-invalid={invalid || undefined}
        aria-describedby={`${testId}-range`}
        compact
        type="number"
        min={min}
        max={max}
        value={draft ?? String(value)}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== "" && Number.isInteger(n) && n >= min && n <= max) onChange(n);
        }}
        onBlur={() => setDraft(null)}
        data-testid={testId}
      />
      {/* the allowed range, always shown; stressed while the typed value is outside it */}
      <span
        id={`${testId}-range`}
        className={clsx("col-start-2 text-[11.5px]", invalid ? "text-err" : "text-neutral-400")}
      >
        {min}–{max}
      </span>
    </label>
  );
}

function AddWidget({
  level,
  onAdd,
}: {
  level: Parameters<typeof listViewTypes>[0];
  onAdd(viewType: string): void;
}) {
  const { t } = useTranslation();
  const { open, toggle, setOpen, ref } = usePopover<HTMLDivElement>();
  return (
    <div ref={ref} className="relative">
      <Button
        variant="secondary"
        size="sm"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="add-widget"
      >
        <Plus size={14} aria-hidden /> {t("dashboards.addWidget")}
      </Button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-9 z-40 w-56 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
        >
          {listViewTypes(level).map((vt) => (
            <li key={vt.id}>
              <button
                type="button"
                role="menuitem"
                data-view-type={vt.id}
                className="w-full rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  onAdd(vt.id);
                  setOpen(false);
                }}
              >
                {t(vt.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
